import {
  DEFAULT_LANGUAGE,
  getStoredLanguage,
  normalizeLanguage,
} from "@/i18n/catalogs";

const currencyLocales = {
  en: { INR: "en-US", AED: "en-AE" },
  ar: { INR: "ar-IN", AED: "ar-AE" },
};

const normalizeCurrency = (currency) => {
  const code = String(currency || "INR").toUpperCase();
  // Keep the historical INR default for unauthenticated/legacy callers, but
  // preserve any valid ISO-4217 code supplied by tenant metadata. This keeps
  // the formatter generic instead of silently converting every new currency
  // into INR.
  return /^[A-Z]{3}$/.test(code) ? code : "INR";
};

export const localeFor = (language = DEFAULT_LANGUAGE, currency = "INR") => {
  const lang = normalizeLanguage(language);
  const code = normalizeCurrency(currency);
  return currencyLocales[lang][code] || (lang === "ar" ? "ar" : "en");
};

const numberingOptions = (language) =>
  normalizeLanguage(language) === "ar" ? { numberingSystem: "arab" } : {};

export const formatNumber = (value, language = getStoredLanguage(), options = {}) => {
  if (value === null || value === undefined || value === "") return "";
  try {
    return new Intl.NumberFormat(localeFor(language), {
      ...numberingOptions(language),
      ...options,
    }).format(Number(value));
  } catch {
    return String(value);
  }
};

const parseDate = (value) => {
  if (!value) return null;
  const date = value instanceof Date ? value : new Date(value);
  return Number.isNaN(date.getTime()) ? null : date;
};

export const formatDate = (value, language = getStoredLanguage(), options = {}) => {
  if (!value) return "";
  const date = parseDate(value);
  if (!date) return String(value);
  try {
    const { currency, locale, ...intlOptions } = options;
    return new Intl.DateTimeFormat(locale || localeFor(language, currency), {
      ...numberingOptions(language),
      day: "2-digit",
      month: "short",
      year: "numeric",
      ...intlOptions,
    }).format(date);
  } catch {
    return String(value);
  }
};

export const formatDateTime = (value, language = getStoredLanguage(), options = {}) => {
  if (!value) return "";
  const date = parseDate(value);
  if (!date) return String(value);
  try {
    const { currency, locale, ...intlOptions } = options;
    return new Intl.DateTimeFormat(locale || localeFor(language, currency), {
      ...numberingOptions(language),
      day: "2-digit",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
      ...intlOptions,
    }).format(date);
  } catch {
    return String(value);
  }
};

export const formatCurrency = (
  minorUnits,
  currency = "INR",
  language = getStoredLanguage(),
  options = {},
) => {
  if (minorUnits === null || minorUnits === undefined || minorUnits === "") return "";
  const code = normalizeCurrency(currency);
  try {
    const { currency: _ignoredCurrency, ...intlOptions } = options;
    return new Intl.NumberFormat(localeFor(language, code), {
      ...numberingOptions(language),
      style: "currency",
      currency: code,
      currencyDisplay: "symbol",
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
      ...intlOptions,
    }).format(Number(minorUnits) / 100);
  } catch {
    return String(minorUnits);
  }
};

export { normalizeLanguage };
